import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { config } from '../config';
import { getKeycloak, initKeycloak } from './keycloak';

const AuthContext = createContext(null);

export function AuthProvider({ mode, children }) {
  const clientId =
    mode === 'platform' ? config.platformClientId : config.tenantClientId;
  const keycloak = getKeycloak(clientId);
  const [initialized, setInitialized] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;

    initKeycloak(clientId)
      .then((auth) => {
        if (!active) {
          return;
        }
        setAuthenticated(Boolean(auth));
        setInitialized(true);
        if (!auth) {
          keycloak.login();
        }
      })
      .catch((err) => {
        if (!active) {
          return;
        }
        setError(err?.message || 'Authentication failed');
        setInitialized(true);
        keycloak.login();
      });

    keycloak.onTokenExpired = () => {
      keycloak.updateToken(30).catch(() => keycloak.login());
    };

    return () => {
      active = false;
    };
  }, [clientId, keycloak]);

  const getToken = useCallback(async () => {
    if (!keycloak.authenticated) {
      return null;
    }
    await keycloak.updateToken(30).catch(() => keycloak.login());
    return keycloak.token;
  }, [keycloak]);

  const logout = useCallback(() => {
    keycloak.logout({ redirectUri: window.location.origin });
  }, [keycloak]);

  const value = useMemo(
    () => ({
      mode,
      keycloak,
      initialized,
      authenticated,
      getToken,
      logout,
      profile: keycloak.tokenParsed || null,
    }),
    [mode, keycloak, initialized, authenticated, getToken, logout],
  );

  if (!initialized) {
    return (
      <div className="auth-loading">
        <div className="spinner" />
        <p>Signing in…</p>
      </div>
    );
  }

  if (!authenticated) {
    return (
      <div className="auth-loading">
        <div className="spinner" />
        <p>Redirecting to login…</p>
        {error && <p style={{ color: '#b91c1c', fontSize: '0.875rem' }}>{error}</p>}
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
